import { Box, Slider, IconButton } from "@mui/material";
import React, { useEffect, useState } from "react";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeDownIcon from "@mui/icons-material/VolumeDown";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

const Volume1Widget = ({ widget, onValueUpdate }) => {
  const [v0, setV0] = useState({});
  const [volume, setVolume] = useState(0);
  const [w1, setW1] = useState({});
  const [minMax, setMinMax] = useState({ min: 0, max: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const sliderRef = React.useRef(null);

  useEffect(() => {
    setV0(widget);
    if (widget?.items?.$w1) {
      setMinMax({
        min: widget.items.$w1.min ?? 0,
        max: widget.items.$w1.max ?? 100,
      });
      setVolume(Number(widget.items.$w1.value));
    }
    return () => {};
  }, [widget]);

  useEffect(() => {
    if (isDragging && sliderRef.current) {
      const rect = sliderRef.current.getBoundingClientRect();
      const left = rect.left + (volume / 100) * rect.width;
      setPosition({
        top: rect.top - 40,
        left: left,
      });
    }
  }, [volume, isDragging]);

  const handleValueUpdate = (data) => {
    const adjustedValue = Math.min(Math.max(data, minMax.min), minMax.max);
    const updatedWidget = {
      ...v0.items.$w1,
      value: adjustedValue,
    };
    setVolume(adjustedValue);
    onValueUpdate(updatedWidget);
  };

  const handleVolumeChange = (event, newValue) => {
    event.stopPropagation();
    handleValueUpdate(newValue);
  };

  const handleMouseEvents = (event) => {
    event.stopPropagation();
  };

  const handleDragStart = (event) => {
    setIsDragging(true);
    handleMouseEvents(event);
  };

  const handleDragEnd = (event) => {
    setIsDragging(false);
    handleMouseEvents(event);
  };

  const toggleMute = () => {
    const newVolume = volume === 0 ? minMax.max : 0;
    handleValueUpdate(newVolume);
  };

  const increaseVolume = () => {
    const newVolume = Math.min(volume + 10, minMax.max);
    handleValueUpdate(newVolume);
  };

  const decreaseVolume = () => {
    const newVolume = Math.max(volume - 10, minMax.min);
    handleValueUpdate(newVolume);
  };

  const VolumeIcon =
    volume === 0 ? VolumeOffIcon : volume < 50 ? VolumeDownIcon : VolumeUpIcon;

  return (
    <Box
      ref={sliderRef}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
        position: "relative",
        gap: 1,
        px: 1,
      }}
      onMouseDown={handleMouseEvents}
      onMouseUp={handleMouseEvents}
      onTouchStart={handleMouseEvents}
      onTouchEnd={handleMouseEvents}
      onClick={handleMouseEvents}
    >
      <IconButton
        onClick={toggleMute}
        size="small"
        sx={{
          color: volume === 0 ? "text.disabled" : "primary.main",
        }}
      >
        <VolumeIcon />
      </IconButton>

      <IconButton
        onClick={decreaseVolume}
        size="small"
        sx={{ color: "primary.main" }}
      >
        <RemoveIcon />
      </IconButton>

      <IconButton
        onClick={increaseVolume}
        size="small"
        sx={{ color: "primary.main" }}
      >
        <AddIcon />
      </IconButton>

      {w1 && (
        <Slider
          value={volume}
          onChange={handleVolumeChange}
          aria-labelledby="volume-slider"
          min={0}
          max={100}
          onMouseDown={handleDragStart}
          onMouseUp={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchEnd={handleDragEnd}
          onMouseLeave={handleDragEnd}
          sx={{
            width: "100%",
            height: 4,
            padding: "15px 0",
            "& .MuiSlider-thumb": {
              width: 12,
              height: 12,
              backgroundColor: "#fff",
              "&:hover, &.Mui-focusVisible": {
                boxShadow: "0px 0px 0px 8px rgba(25, 118, 210, 0.16)",
              },
            },
            "& .MuiSlider-track": {
              height: 4,
              backgroundColor: "primary.main",
            },
            "& .MuiSlider-rail": {
              height: 4,
              opacity: 0.2,
              backgroundColor: "#bfbfbf",
            },
          }}
        />
      )}
    </Box>
  );
};

export default Volume1Widget;
